import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Typography,
  Button,
  Grid,
  Box,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert
} from '@mui/material';
import { Add, Delete, Edit, ArrowBack } from '@mui/icons-material';
import { getSliders, createSlider, updateSlider, deleteSlider } from '../../services/sliderService';
import { useNotificacao } from '../../contexts/NotificacaoContext';

const GerenciarSlidersPage = () => {
  const { instituicaoId } = useParams();
  const [sliders, setSliders] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentSlider, setCurrentSlider] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    linkUrl: '',
    ordem: 0,
    imagem: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  
  const { adicionarNotificacao } = useNotificacao();
  const baseUrl = process.env.REACT_APP_API_BASE_URL || '';

  const fetchSliders = useCallback(async () => {
    try {
      const data = await getSliders(instituicaoId);
      setSliders(data);
    } catch (error) {
      console.error('Erro ao buscar sliders:', error);
      adicionarNotificacao('Erro ao carregar sliders', 'error');
    }
  }, [instituicaoId, adicionarNotificacao]);

  useEffect(() => {
    fetchSliders();
  }, [fetchSliders]);

  const resetForm = useCallback(() => {
    setFormData({
      titulo: '',
      descricao: '',
      linkUrl: '',
      ordem: 0,
      imagem: null
    });
    setImagePreview(null);
    setErrors({});
    setSubmitError('');
    setSubmitSuccess('');
  }, []);

  const handleOpenDialog = useCallback((slider = null) => {
    if (slider) {
      setFormData({
        titulo: slider.titulo || '',
        descricao: slider.descricao || '',
        linkUrl: slider.linkUrl || '',
        ordem: slider.ordem || 0,
        imagem: null
      });
      setImagePreview(`${baseUrl}/${slider.imagemUrl}`);
    } else {
      resetForm();
    }
    setCurrentSlider(slider);
    setOpenDialog(true);
  }, [baseUrl, resetForm]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentSlider(null);
    resetForm();
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.titulo.trim()) {
      newErrors.titulo = 'Título é obrigatório.';
    }

    if (!currentSlider && !formData.imagem) {
      newErrors.imagem = 'A imagem é obrigatória.';
    }

    if (formData.linkUrl.trim() && !isValidURL(formData.linkUrl)) {
      newErrors.linkUrl = 'URL do Link inválida.';
    }

    if (formData.ordem < 0 || !Number.isInteger(Number(formData.ordem))) {
      newErrors.ordem = 'Ordem deve ser um número inteiro não negativo.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidURL = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'imagem') {
      const file = files?.[0];
      if (file) {
        setFormData(prev => ({ ...prev, imagem: file }));
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
      if (errors.imagem) {
        setErrors(prev => ({ ...prev, imagem: null }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
      }
    }
  };

  const handleSaveSlider = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('titulo', formData.titulo);
      formDataToSend.append('descricao', formData.descricao);
      formDataToSend.append('linkUrl', formData.linkUrl);
      formDataToSend.append('ordem', formData.ordem.toString());
      if (formData.imagem) {
        formDataToSend.append('imagem', formData.imagem);
      }

      if (currentSlider) {
        await updateSlider(currentSlider.id, formDataToSend);
        setSubmitSuccess('Slider atualizado com sucesso.');
        adicionarNotificacao('Slider atualizado com sucesso', 'success');
      } else {
        await createSlider(formDataToSend);
        setSubmitSuccess('Slider criado com sucesso.');
        adicionarNotificacao('Slider criado com sucesso', 'success');
      }

      await fetchSliders();
      handleCloseDialog();
    } catch (error) {
      console.error('Erro ao salvar slider:', error);
      setSubmitError('Falha ao salvar o slider. Por favor, tente novamente.');
      adicionarNotificacao('Erro ao salvar slider', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (sliderId) => {
    if (window.confirm('Tem certeza que deseja excluir este slider?')) {
      try {
        await deleteSlider(sliderId);
        adicionarNotificacao('Slider removido com sucesso', 'success');
        await fetchSliders();
      } catch (error) {
        console.error('Erro ao deletar slider:', error);
        adicionarNotificacao('Erro ao remover slider', 'error');
      }
    }
  };

  return (
    <Box>
      {/* Cabeçalho com botão voltar */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          component={Link}
          to="/dashboard"
        >
          Voltar ao Dashboard
        </Button>
        <Typography variant="h4">
          Gerenciar Sliders
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
      >
        Adicionar Novo Slide
      </Button>

      {sliders.length === 0 && <Typography>Nenhum slider encontrado.</Typography>}

      <Grid container spacing={2}>
        {sliders.map((slider) => (
          <Grid item xs={12} sm={6} md={4} key={slider.id}>
            <Card>
              <CardMedia
                component="img"
                height="200"
                image={`${baseUrl}/${slider.imagemUrl}`}
                alt={slider.titulo || `Slider ${slider.id}`}
              />
              <CardContent>
                <Typography variant="h6">{slider.titulo || 'Sem título'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {slider.descricao}
                </Typography>
                {slider.linkUrl && (
                  <Typography variant="body2" color="primary">
                    Link: {slider.linkUrl}
                  </Typography>
                )}
                <Typography variant="body2">
                  Ordem: {slider.ordem}
                </Typography>
              </CardContent>
              <CardActions>
                <IconButton onClick={() => handleOpenDialog(slider)} color="primary">
                  <Edit />
                </IconButton>
                <IconButton onClick={() => handleDelete(slider.id)} color="error">
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {currentSlider ? 'Editar Slider' : 'Adicionar Novo Slider'}
        </DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          {submitSuccess && <Alert severity="success" sx={{ mb: 2 }}>{submitSuccess}</Alert>}
          <Box component="form" noValidate>
            <TextField
              autoFocus
              margin="dense"
              name="titulo"
              label="Título"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.titulo}
              onChange={handleChange}
              error={Boolean(errors.titulo)}
              helperText={errors.titulo}
            />
            <TextField
              margin="dense"
              name="descricao"
              label="Descrição"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              minRows={3}
              value={formData.descricao}
              onChange={handleChange}
              error={Boolean(errors.descricao)}
              helperText={errors.descricao}
            />
            <Button
              variant="contained"
              component="label"
              sx={{ mt: 2 }}
            >
              {formData.imagem ? 'Alterar Imagem' : 'Selecionar Imagem'}
              <input
                type="file"
                accept="image/*"
                hidden
                name="imagem"
                onChange={handleChange}
              />
            </Button>
            {errors.imagem && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {errors.imagem}
              </Typography>
            )}
            {imagePreview && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Pré-visualização:</Typography>
                <img 
                  src={imagePreview} 
                  alt="Pré-visualização" 
                  style={{ 
                    width: '100%', 
                    maxHeight: '300px', 
                    objectFit: 'cover',
                    borderRadius: '4px'
                  }} 
                />
              </Box>
            )}
            <TextField
              margin="dense"
              name="linkUrl"
              label="URL do Link"
              type="url"
              fullWidth
              variant="outlined"
              value={formData.linkUrl}
              onChange={handleChange}
              error={Boolean(errors.linkUrl)}
              helperText={errors.linkUrl}
              sx={{ mt: 2 }}
            />
            <TextField
              margin="dense"
              name="ordem"
              label="Ordem"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.ordem}
              onChange={handleChange}
              error={Boolean(errors.ordem)}
              helperText={errors.ordem}
              inputProps={{ min: 0 }}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveSlider}
            color="primary"
            variant="contained"
            disabled={isSubmitting}
          >
            {currentSlider ? 'Atualizar' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GerenciarSlidersPage;